import data from './challenges.json';
import type { Challenge, Deal, Path, Work } from './types';

const challenges = data.challenges as unknown as Challenge[];
const byId = new Map(challenges.map((c) => [c.id, c]));

/**
 * Retired exercises (activities audit, well/product/22 §3.5: One-Minute Movie,
 * and three near-duplicates folded into their better twin) stay in the data so
 * an old post still finds its title and card, and nowhere else: never dealt,
 * tiled, listed or put on the road.
 */
const live = (id: string) => !byId.get(id)?.retired;

export const RESET: Record<Path, string[]> = {
  writing: data.reset.filter(live),
  drawing: data.resetDrawing.filter(live),
};

/** Any exercise by id, retired ones included, so what was made with one still resolves. */
export function getChallenge(id: string): Challenge | undefined {
  return byId.get(id);
}

/** The whole data, retired ones included. Anything that deals or lists wants liveChallenges. */
export function allChallenges(): Challenge[] {
  return challenges;
}

/** Every exercise that can still be dealt, tiled or listed. A starter has its own screen and is none of those. */
export function liveChallenges(): Challenge[] {
  return challenges.filter((c) => !c.retired && !c.starter);
}

/** The exercise dealt on count-up day `day` (1-based) for a path. Past Day 14 the reset wraps; Days 15–30 are not sequenced yet (10 §18). */
export function challengeForDay(day: number, path: Path): Challenge {
  const seq = RESET[path];
  const id = seq[(Math.max(1, day) - 1) % seq.length];
  return byId.get(id)!;
}

export function dayOf(challengeId: string, path: Path): number | undefined {
  const i = RESET[path].indexOf(challengeId);
  return i === -1 ? undefined : i + 1;
}

/** One random draw from every key in the deck. `avoid` re-deals away from the current draw. */
export function deal(challenge: Challenge, avoid?: Deal): Deal {
  const out: Deal = {};
  for (const [key, options] of Object.entries(challenge.deck)) {
    if (options.length === 0) continue;
    let pick = options[Math.floor(Math.random() * options.length)];
    if (avoid && options.length > 1 && pick === avoid[key]) pick = options[(options.indexOf(pick) + 1) % options.length];
    out[key] = pick;
  }
  return out;
}

/** Replace {key} placeholders with the deal. Keys with no draw are left readable ("a thing"). */
export function fill(template: string, d: Deal): string {
  return template.replace(/\{([^}]+)\}/g, (_, k: string) => d[k] ?? k);
}

/**
 * The headline keys no deck supplies: they are counted off the work at the
 * moment the card is made, rather than dealt beforehand.
 *
 * `{n}` is how many things the person actually made and `{s}` is its plural, so
 * "{n} terrible idea{s}" reads right at one and at ten. Before these existed
 * the number was typed into the template, and every list card said "10" whatever
 * was on it — so the first card a first-time person ever made announced ten
 * ideas above their three.
 */
export const RUNTIME_KEYS = ['n', 's'];

/** `{n}` and `{s}` for a piece of work: how many entries it actually has. */
export function countDeal(work: Work): Deal {
  const n = work.lines?.filter((l) => l.trim()).length ?? work.pairs?.filter((p) => p.name.trim()).length ?? 0;
  return { n: String(n), s: n === 1 ? '' : 's' };
}

export function headlineFor(challenge: Challenge, d: Deal): string {
  return fill(challenge.card.headline, d);
}

export function promptFor(challenge: Challenge, d: Deal): string {
  return fill(challenge.prompt, d);
}

export function inviteFor(challenge: Challenge, d: Deal): string {
  return fill(challenge.card.invite || challenge.prompt, d);
}

export function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}m ${String(s).padStart(2, '0')}s`;
}
